import { useEffect, useRef, useState } from "react";

import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import { finishSsoLogin } from "@/api/sso";
import { useAuthStore } from "@/store/authStore";

/** Messages are deliberately vague: the API refuses without saying why. */
const MESSAGES: Record<string, string> = {
  sso_login_refused:
    "Cette identité n'est associée à aucun compte Cortex. Demandez une invitation à votre administrateur.",
  invalid_or_expired_state: "La demande de connexion a expiré. Recommencez.",
  state_is_for_linking: "Ce lien correspond à une association de compte, pas à une connexion.",
  sso_temporarily_unavailable: "Service d'authentification momentanément indisponible.",
};

export function SsoCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const signInWithTokens = useAuthStore((s) => s.signInWithTokens);
  const [error, setError] = useState<string | null>(null);
  // React 18 mounts effects twice in dev; the state is single-use server-side,
  // so a second exchange would always fail.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const idpError = params.get("error");

    if (idpError) {
      setError(
        idpError === "access_denied"
          ? "Connexion annulée."
          : "Le fournisseur d'identité a refusé la demande.",
      );
      return;
    }
    if (!code || !state) {
      setError("Réponse incomplète du fournisseur d'identité.");
      return;
    }

    finishSsoLogin(state, code)
      .then(async (tp) => {
        await signInWithTokens(tp);
        navigate("/", { replace: true });
      })
      .catch((e) => {
        const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
          ?.detail;
        const key = typeof detail === "string" ? detail : "";
        setError(MESSAGES[key] ?? "La connexion a échoué.");
      });
  }, [params, navigate, signInWithTokens]);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 420, width: "100%" }}>
        {error === null ? (
          <>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Connexion en cours…
            </Typography>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={() => navigate("/login", { replace: true })}>
              Retour à la connexion
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
